import type { Dispatch, SubmitEventHandler } from 'react';
import type { UserLocalStorage } from '../../types/localStorage';

import { useForm } from '@tanstack/react-form';
import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';
import { z } from 'zod';

import { setFormSubmitErrors } from '../../form-hooks/lib/helpers';
import {
  authButtonsData,
  generateRedirectUrl,
  getLoginErrorText,
  getMaskedOrganizationName,
} from '../../lib/helpers';
import { simplePasswordSchema } from '../../lib/zodHelpers';

import Button from '../../components/Button/Button';
import LinkButton from '../../components/LinkButton/LinkButton';
import Loader from '../../components/Loader/Loader';

import { ModalInstanceContext } from '../../contexts/ModalContext';

import { WarningIcon } from '../../images/shapes';

import FormInput from './FormInput/FormInput';

const initialValues = { password: '' };

interface LoginModalFormProps {
  /**
   * Login form on submit handler
   */
  onSubmit: (
    values: { password: string },
    setUser: Dispatch<UserLocalStorage>,
  ) => Promise<[string | null, Record<string, string | string[]> | null]>;
  /**
   * User email
   */
  email: string;
}

const LoginModalForm = ({ onSubmit, email }: LoginModalFormProps) => {
  const modalInstance = use(ModalInstanceContext);
  const { t } = useTranslation();
  const [error, setError] = useState<string | string[]>();
  const [isLogging, setIsLogging] = useState(false);
  const [, setUser] = useLocalStorageState('cms.user');

  const loginSentRef = useRef<boolean>(null);

  const validationSchema = useMemo(
    () =>
      z.object({
        password: simplePasswordSchema(t),
      }),
    [t],
  );

  /**
   * Due to circular dependencies, this form cannot use custom "useAppForm" function.
   * That's the reason why we use "useForm" directly and using custom input component.
   */
  const form = useForm({
    defaultValues: initialValues,
    onSubmit: async ({ value, formApi }) => {
      setIsLogging(true);
      const [token, errors] = await onSubmit(value, setUser);

      if (errors && Object.keys(errors).length > 0) {
        if (errors.global) {
          setError(errors.global);
        }
        setFormSubmitErrors(formApi, errors);
      } else {
        modalInstance.resolve?.(token);
      }

      setIsLogging(false);
    },
    validators: {
      onSubmit: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  const handleWindowMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data.type !== 'login') return;
      const params = new URLSearchParams(event.data.params);
      const status = params.get('status');
      const token = params.get('token');
      const registered = params.get('registered');

      let errorMessage = '';
      if (status) {
        errorMessage = getLoginErrorText(status, t);
      }
      if (token) {
        try {
          const userData = window.atob(token);
          const user = JSON.parse(userData);
          setUser(user);

          if (!loginSentRef.current) {
            TagManager.dataLayer({
              dataLayer: {
                event: 'login',
                user_id: user.data?.id,
                organization_id: user.data?.organization,
                organization_name: getMaskedOrganizationName(
                  user.data?.organization_name,
                ),
                plan_id: user.data?.limits_plan?.id,
                plan_name: user.data?.limits_plan?.name,
                space_id: user.data?.default_space?.id,
                user_roles: user.data?.roles,
              },
            });
            loginSentRef.current = true;
          }

          if (registered)
            TagManager.dataLayer({
              dataLayer: {
                event: 'sign_up',
              },
            });

          modalInstance.resolve?.(user.token);
        } catch {
          errorMessage = t('AppSumo.TokenNotValid');
        }
      }
      if (errorMessage) {
        toast.error(errorMessage);
        setError(errorMessage);
      }
    },
    [t, setUser, modalInstance],
  );

  const openNewAuthWindow = useCallback(
    (url: string) => {
      const newWindow = window.open(
        `${url}?redirectUri=${generateRedirectUrl('/loginBlank.html', true)}`,
        '',
        'width=600,height=400,left=200,top=200',
      );
      if (
        !newWindow ||
        newWindow.closed ||
        typeof newWindow.closed == 'undefined'
      ) {
        toast.error(t('Login.EnablePopups'));
        return;
      }
      window.addEventListener('message', handleWindowMessage);
    },
    [handleWindowMessage, t],
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('message', handleWindowMessage);
    };
  }, [handleWindowMessage]);

  const onLogOut = useCallback(() => {
    modalInstance.resolve?.();
  }, [modalInstance]);

  return (
    <form
      id="login-modal-form"
      className="relative w-full -mb-8"
      onSubmit={handleSubmit}
      noValidate={true}
    >
      <div className="mb-2">
        <Trans
          i18nKey="Global.SessionExpiredInfo"
          values={{ email }}
          components={{ 1: <span className="text-blue" /> }}
        />
      </div>
      <div className="flex flex-row items-center gap-2 w-full">
        <form.Field name="password">
          {(field) => (
            <FormInput
              field={field}
              type="password"
              placeholder={t('Global.Password')}
            />
          )}
        </form.Field>

        <form.Subscribe
          selector={({ fieldMeta }) => fieldMeta?.password?.errors}
        >
          {(errors: any) => (
            <Button
              buttonSize="md"
              type="submit"
              disabled={isLogging}
              iconImage={
                isLogging ? <Loader size="small" type="spinner-grid" /> : null
              }
              additionalClasses={twMerge(
                'w-fit whitespace-nowrap',
                errors?.length > 0 ? 'mb-6' : '',
              )}
            >
              {t('Global.LogIn')}
            </Button>
          )}
        </form.Subscribe>
      </div>
      {error && (
        <div className="text-red pt-2 inline-flex">
          <WarningIcon className="h-5 w-5 mr-2" />
          <div className="block text-left">{error}</div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
        {authButtonsData.map((auth) => (
          <Button
            key={auth.name}
            iconImage={auth.logo}
            buttonSize="md"
            iconPosition="start"
            buttonColor="grayBordered"
            onClick={() => openNewAuthWindow(auth.url)}
            additionalClasses=" text-sm!"
          >
            {t('Global.ContinueWith')} {auth.name}
          </Button>
        ))}
      </div>
      <div className="flex justify-center items-center">
        <LinkButton
          buttonSize="sm"
          onClick={onLogOut}
          link={'/logout'}
          buttonColor="borderless"
          additionalClasses="pt-5"
        >
          {t('Global.LogOut')}
        </LinkButton>
      </div>
    </form>
  );
};
export default LoginModalForm;
